import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/config';
import { useAuth } from '../hooks/useAuth';
import { useParty } from '../hooks/useParty';
import { useFriends } from '../hooks/useFriends';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import PartyCodeDisplay from '../components/PartyCodeDisplay';
import { useLanguage } from '../hooks/useLanguage';
import { toast } from 'react-hot-toast';
import Map from '../components/Map';
import { 
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Event as EventIcon,
  LocationOn as LocationOnIcon,
  Info as InfoIcon,
  PersonAdd as PersonAddIcon,
  Check as CheckIcon,
  Chat as ChatIcon,
  ListAlt as ListAltIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  MyLocation as MyLocationIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

// Custom CSS for map
const mapStyles = `
  .leaflet-container {
    width: 100% !important;
    height: 100% !important;
    z-index: 1 !important;
    max-width: 100% !important;
    overflow: hidden !important;
    position: relative !important;
  }
  
  .leaflet-control-container {
    display: none !important;
  }
  
  .leaflet-pane {
    z-index: 1 !important;
  }
  
  .leaflet-tile-pane {
    z-index: 1 !important;
  }
  
  .leaflet-overlay-pane {
    z-index: 2 !important;
  }
  
  .leaflet-marker-pane {
    z-index: 3 !important;
  }
  
  .leaflet-popup-pane {
    z-index: 4 !important;
  }
  
  .leaflet-map-pane {
    z-index: 1 !important;
  }
`;

// Add a helper function to generate a unique ID
const generateUniqueId = () => {
  // Check if crypto.randomUUID is available
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  
  // Fallback method using Math.random
  return 'id-' + 
    Date.now().toString(36) + '-' + 
    Math.floor(Math.random() * 1000000).toString(36);
};

// Helper function to detect iOS devices
const isIOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
};

// Helper function to detect Safari browser
const isSafari = () => {
  const userAgent = navigator.userAgent;
  return /^((?!chrome|android).)*safari/i.test(userAgent);
};

// Helper function to check if we're using HTTPS
const isHttps = () => {
  return window.location.protocol === 'https:';
};

// Helper function to open iOS settings directly (this works on some iOS versions)
const openIOSSettings = () => {
  // Try to open the Settings app directly
  window.location.href = 'app-settings:';
  
  // Alternative URLs that might work on different iOS versions
  setTimeout(() => {
    window.location.href = 'prefs:root=Privacy&path=LOCATION';
  }, 300);
};

// Helper function to request location permission with better iOS handling
const requestLocationPermission = () => {
  return new Promise((resolve, reject) => {
    // For iOS Safari, we need a special approach
    if (isIOS() && isSafari()) {
      console.log('Using iOS Safari specific approach');
      
      // Create a more aggressive approach for iOS Safari
      const maxAttempts = 3;
      let currentAttempt = 0;
      
      const attemptLocationRequest = () => {
        currentAttempt++;
        console.log(`Location request attempt ${currentAttempt} of ${maxAttempts}`);
        
        try {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              console.log(`Attempt ${currentAttempt} succeeded`);
              resolve('granted');
            },
            (error) => {
              console.log(`Attempt ${currentAttempt} failed:`, error);
              
              if (error.code === 1) { // Permission denied
                if (currentAttempt < maxAttempts) {
                  // Try again with a slight delay
                  setTimeout(attemptLocationRequest, 300);
                } else {
                  // After all attempts failed, provide clear instructions
                  reject(new Error('Location permission denied. You need to enable location services in your device settings.'));
                }
              } else if (error.code === 2) { // Position unavailable
                reject(new Error('Could not determine your location. Please check your device settings.'));
              } else if (error.code === 3) { // Timeout
                if (currentAttempt < maxAttempts) {
                  // Try again with a longer timeout
                  setTimeout(attemptLocationRequest, 300);
                } else {
                  reject(new Error('Location request timed out. Please try again.'));
                }
              } else {
                reject(error);
              }
            },
            { 
              enableHighAccuracy: true, 
              timeout: 10000 + (currentAttempt * 5000), // Increase timeout with each attempt
              maximumAge: 0 
            }
          );
        } catch (e) {
          console.error("Error calling getCurrentPosition:", e);
          reject(e);
        }
      };
      
      // Start the first attempt
      attemptLocationRequest();
    } 
    // For other browsers, use the standard approach
    else {
      // First check if permissions API is available (newer browsers)
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'geolocation' })
          .then(permissionStatus => {
            console.log('Geolocation permission status:', permissionStatus.state);
            
            if (permissionStatus.state === 'granted') {
              resolve('granted');
            } else if (permissionStatus.state === 'prompt') {
              // We need to request permission
              triggerLocationRequest(resolve, reject);
            } else if (permissionStatus.state === 'denied') {
              reject(new Error('Location permission denied'));
            }
            
            // Listen for permission changes
            permissionStatus.onchange = () => {
              console.log('Permission changed to:', permissionStatus.state);
              if (permissionStatus.state === 'granted') {
                resolve('granted');
              }
            };
          })
          .catch(error => {
            console.warn('Permission API error, falling back to direct request', error);
            // Fall back to direct request
            triggerLocationRequest(resolve, reject);
          });
      } else {
        // Permissions API not available, fall back to direct request
        console.log('Permissions API not available, using direct request');
        triggerLocationRequest(resolve, reject);
      }
    }
  });
};

// Helper function to trigger the actual location request
const triggerLocationRequest = (resolve, reject) => {
  const options = {
    enableHighAccuracy: true,
    timeout: isIOS() ? 30000 : 20000,
    maximumAge: 0
  };
  
  navigator.geolocation.getCurrentPosition(
    (position) => {
      console.log('Location permission granted');
      resolve('granted');
    },
    (error) => {
      console.error('Location request error:', error);
      if (error.code === 1) {
        reject(new Error('Location permission denied'));
      } else if (error.code === 2) {
        reject(new Error('Location unavailable'));
      } else if (error.code === 3) {
        reject(new Error('Location request timed out'));
      } else {
        reject(new Error('Unknown location error'));
      }
    },
    options
  );
};

// Add this function component for the check-in feature
const CheckInMarker = ({ position, userName, isHost, accuracy }) => {
  const { t } = useLanguage();
  
  // Make sure we have a valid position
  if (!position || !position.lat || !position.lng) {
    console.warn('Invalid position for CheckInMarker:', position);
    return null;
  }
  
  return (
    <>
      <Marker position={[position.lat, position.lng]}>
        <Popup>
          {userName || t.unknown} {isHost ? `(${t.host})` : ''} <br />
          {t.checkedInHere}
        </Popup>
      </Marker>
      {accuracy && (
        <Circle 
          center={[position.lat, position.lng]} 
          radius={accuracy} 
          pathOptions={{ 
            color: isHost ? '#1976d2' : '#9c27b0',
            fillColor: isHost ? '#1976d2' : '#9c27b0',
            fillOpacity: 0.1
          }} 
        />
      )}
    </>
  );
};

// Helper function to generate a random code (6 uppercase letters and numbers)
const generateRandomCode = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

const PartyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const {
    deleteParty, 
    inviteToParty, 
    rsvpToParty, 
    addItem, 
    removeItem, 
    addMessage 
  } = useParty();
  const { sendFriendRequest, friends } = useFriends();
  
  const [party, setParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [newMessage, setNewMessage] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [newItemBroughtBy, setNewItemBroughtBy] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState('');
  const messagesEndRef = useRef(null);
  const [attendeeDetails, setAttendeeDetails] = useState({});
  const [friendRequestSent, setFriendRequestSent] = useState({});
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const [checkedInUsers, setCheckedInUsers] = useState([]);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  // Fetch party data and set up real-time subscriptions
  useEffect(() => {
    if (!id) return;

    const fetchParty = async () => {
      try {
        const { data: partyData, error: fetchError } = await supabase
          .from('parties')
          .select('*')
          .eq('id', id)
          .single();
        
        if (fetchError) throw fetchError;
        
        if (partyData) {
          setParty(partyData);
          
          // Set RSVP status if user has responded
          if (user && partyData.rsvp_responses && partyData.rsvp_responses[user.id]) {
            setRsvpStatus(partyData.rsvp_responses[user.id]);
          }
          
          setLoading(false);
        } else {
          setError('Party not found');
          setLoading(false);
        }
      } catch (err) {
        setError('Error loading party: ' + err.message);
        setLoading(false);
      }
    };

    fetchParty();

    // Set up real-time subscription for party updates
    const partySubscription = supabase
      .channel(`party-${id}`)
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'parties',
          filter: `id=eq.${id}`
        }, 
        (payload) => {
          console.log('Party updated via real-time:', payload);
          if (payload.new) {
            // Update the party state with the new data
            setParty(prevParty => {
              // If we already have a newer version of the party data, don't update
              if (prevParty && 
                  prevParty.messages && 
                  payload.new.messages && 
                  prevParty.messages.length > payload.new.messages.length) {
                return prevParty;
              }
              return payload.new;
            });
            
            // Update RSVP status if needed
            if (user && payload.new.rsvp_responses && payload.new.rsvp_responses[user.id]) {
              setRsvpStatus(payload.new.rsvp_responses[user.id]);
            }
            
            // Scroll to bottom if new messages were added
            if (messagesEndRef.current && tabValue === 1) {
              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }
      )
      .on('postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'parties',
          filter: `id=eq.${id}`
        },
        (payload) => {
          console.log('Party deleted via real-time:', payload);
          // Set a friendly error message
          setError('This party has been deleted by the host.');
          // Clear the party data
          setParty(null);
          // Start countdown for redirect
          setRedirectCountdown(5);
        }
      )
      .subscribe();

    // Set up a separate channel for custom party update notifications
    const notificationSubscription = supabase
      .channel('party_updates')
      .on('broadcast', { event: 'party_updates' }, (payload) => {
        console.log('Received broadcast notification:', payload);
        
        // If this notification is for our party, refresh the data
        if (payload.payload && payload.payload.party_id === id) {
          console.log('Refreshing party data due to broadcast notification');
          fetchParty();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(partySubscription);
      supabase.removeChannel(notificationSubscription);
    };
  }, [id, user, tabValue]);

  // Fetch attendee details
  useEffect(() => {
    if (!party || !id) return;

    const fetchAttendeeDetails = async () => {
      try {
        const details = { ...attendeeDetails };
        
        // Fetch host details first
        const { data: hostData, error: hostError } = await supabase
          .from('users')
          .select('id, display_name, email, avatar_url')
          .eq('id', party.host_id || party.host)
          .single();
        
        if (!hostError && hostData) {
          // Get the public URL for the host's avatar
          if (hostData.avatar_url) {
            const { data: avatarData } = await supabase
              .storage
              .from('avatars')
              .getPublicUrl(hostData.avatar_url);
            
            if (avatarData) {
              hostData.avatar_url = avatarData.publicUrl;
            }
          }
          
          details[hostData.id] = {
            ...hostData,
            is_host: true
          };
        }
        
        // Fetch details for each attendee
        if (party.attendees && party.attendees.length > 0) {
          for (const attendeeId of party.attendees) {
            // Skip if we already have the host details
            if (attendeeId === (party.host_id || party.host) && details[attendeeId]) {
              continue;
            }
            
            const { data, error } = await supabase
              .from('users')
              .select('id, display_name, email, avatar_url')
              .eq('id', attendeeId)
              .single();
            
            if (!error && data) {
              // Get the public URL for the attendee's avatar
              if (data.avatar_url) {
                const { data: avatarData } = await supabase
                  .storage
                  .from('avatars')
                  .getPublicUrl(data.avatar_url);
                
                if (avatarData) {
                  data.avatar_url = avatarData.publicUrl;
                }
              }
              
              details[attendeeId] = {
                ...data,
                is_host: attendeeId === (party.host_id || party.host)
              };
            }
          }
        }
        
        setAttendeeDetails(details);
      } catch (err) {
        console.error('Error fetching attendee details:', err);
      }
    };

    fetchAttendeeDetails();
  }, [id, party]);

  // Scroll to bottom of messages when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && tabValue === 1) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [party?.messages, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;

    try {
      setSendingMessage(true);
      
      // Get the user's profile data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('display_name, avatar_url')
        .eq('id', user.id)
        .single();
        
      if (userError) {
        console.error('Error fetching user data:', userError);
      }
      
      // Create the message object
      const message = {
        text: newMessage.trim(),
        userId: user.id,
        userName: userData?.display_name || user.user_metadata?.display_name || user.email,
        userPhotoURL: userData?.avatar_url || user.user_metadata?.avatar_url || '',
        createdAt: new Date().toISOString(),
        id: generateUniqueId()
      };
      
      // Clear the input field immediately for better UX
      setNewMessage('');
      
      // Optimistically update the UI
      const updatedParty = { ...party };
      updatedParty.messages = [...(updatedParty.messages || []), message];
      setParty(updatedParty);
      
      // Scroll to the bottom
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      
      // Actually send the message
      const result = await addMessage(id, message);
      console.log('Message sent successfully:', result);
      
      setSendingMessage(false);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message: ' + err.message);
      setSendingMessage(false);
      
      // Revert the optimistic update if there was an error
      fetchParty();
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    try {
      setAddingItem(true);
      
      const item = {
        name: newItemName,
        broughtBy: newItemBroughtBy || user.user_metadata?.display_name || user.email,
        broughtById: newItemBroughtBy ? '' : user.id,
        createdAt: new Date().toISOString()
      };
      
      await addItem(id, item);
      setNewItemName('');
      setNewItemBroughtBy('');
      setOpenItemDialog(false);
      setAddingItem(false);
    } catch (err) {
      console.error('Error adding item:', err);
      setError('Failed to add item: ' + err.message);
      setAddingItem(false);
    }
  };

  const handleRemoveItem = async (item) => {
    try {
      await removeItem(id, item);
    } catch (err) {
      console.error('Error removing item:', err);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    try {
      await inviteToParty(id, inviteEmail);
      setInviteEmail('');
      setOpenInviteDialog(false);
    } catch (err) {
      console.error('Error inviting user:', err);
    }
  };

  const handleRSVP = async (response) => {
    try {
      console.log('Handling RSVP:', response);
      
      // Show some visual feedback that the action is in progress
      setLoading(true);
      
      const result = await rsvpToParty(id, user.id, response);
      console.log('RSVP result:', result);
      
      // Update local state
      setRsvpStatus(response);
      
      // If the party state is managed locally, update it
      if (party) {
        const updatedParty = { ...party };
        
        // Update RSVP responses
        updatedParty.rsvp_responses = updatedParty.rsvp_responses || {};
        updatedParty.rsvp_responses[user.id] = response;
        
        // Update attendees
        updatedParty.attendees = updatedParty.attendees || [];
        if (response === 'attending') {
          if (!updatedParty.attendees.includes(user.id)) {
            updatedParty.attendees.push(user.id);
          }
        } else {
          updatedParty.attendees = updatedParty.attendees.filter(id => id !== user.id);
        }
        
        setParty(updatedParty);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating RSVP:', err);
      setError('Failed to update RSVP: ' + err.message);
      setLoading(false);
    }
  };

  const handleDeleteParty = async () => {
    if (window.confirm('Are you sure you want to delete this party?')) {
      try {
        await deleteParty(id);
        navigate('/');
      } catch (err) {
        console.error('Error deleting party:', err);
      }
    };
  };

  // Helper function to get attendee display name
  const getAttendeeDisplayName = (attendeeId) => {
    if (attendeeDetails[attendeeId]) {
      return attendeeDetails[attendeeId].display_name || attendeeDetails[attendeeId].email || `User ${attendeeId.substring(0, 8)}...`;
    }
    
    // Fallback to ID if details not loaded yet
    return `User ${attendeeId.substring(0, 8)}...`;
  };

  // Helper function to check if an attendee is the host
  const isAttendeeHost = (attendeeId) => {
    if (attendeeDetails[attendeeId]) {
      return attendeeDetails[attendeeId].is_host;
    }
    return attendeeId === party.host_id || attendeeId === party.host;
  };

  // Helper function to check if user is already a friend
  const isAlreadyFriend = (attendeeId) => {
    return friends.some(friend => 
      friend.friend_id === attendeeId || friend.user_id === attendeeId
    );
  };

  // Handle sending a friend request to an attendee
  const handleAddFriend = async (attendeeId) => {
    try {
      if (!attendeeDetails[attendeeId]?.email) {
        setError('Cannot add friend: email not found');
        return;
      }
      
      await sendFriendRequest(attendeeDetails[attendeeId].email);
      
      // Update local state to show the request was sent
      setFriendRequestSent(prev => ({
        ...prev,
        [attendeeId]: true
      }));
    } catch (err) {
      console.error('Error sending friend request:', err);
      setError('Failed to send friend request: ' + err.message);
    }
  };

  // Helper function to fetch the party data
  const fetchParty = async () => {
    try {
      const { data, error } = await supabase
        .from('parties')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      if (data) {
        setParty(data);
      }
    } catch (err) {
      console.error('Error fetching party:', err);
    }
  };

  // Add a separate useEffect for the countdown cleanup
  useEffect(() => {
    let countdownInterval;
    
    if (redirectCountdown !== null) {
      countdownInterval = setInterval(() => {
        setRedirectCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            navigate('/');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [redirectCountdown, navigate]);

  // Function to handle location updates
  const handleUpdateLocation = async () => {
    setIsUpdatingLocation(true);
    setLocationError(null);
    
    try {
      // Check if geolocation is available
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
      }
      
      // Show requesting location message
      toast.success(t.requestingLocation);
      
      // Request permission first
      try {
        await requestLocationPermission();
      } catch (permissionError) {
        console.error('Permission error:', permissionError);
        setLocationError(permissionError.message || t.locationPermissionDenied);
        setIsUpdatingLocation(false);
        return;
      }
      
      // Get current location
      let position;
      try {
        position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos),
            (err) => {
              console.error('Geolocation error:', err);
              if (err.code === 1) {
                reject(new Error(isIOS() ? t.iOSLocationDenied : t.locationPermissionDenied));
              } else if (err.code === 2) {
                reject(new Error(t.locationUnavailable));
              } else if (err.code === 3) {
                reject(new Error(t.locationTimeout));
              } else {
                reject(new Error(t.locationError));
              }
            },
            {
              enableHighAccuracy: true,
              timeout: isIOS() ? 30000 : 20000,
              maximumAge: 0
            }
          );
        });
      } catch (locationError) {
        console.error('Location acquisition error:', locationError);
        setLocationError(locationError.message || t.locationError);
        setIsUpdatingLocation(false);
        return;
      }
      
      // Format position data
      const userPos = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      };
      
      // Update UI
      setUserLocation(userPos);
      
      // Update database
      try {
        const { error } = await supabase
          .from('party_check_ins')
          .upsert({ 
            party_id: id,
            user_id: user.id,
            latitude: userPos.lat,
            longitude: userPos.lng,
            accuracy: userPos.accuracy,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'party_id,user_id'
          });
        
        if (error) throw error;
        
        // Update local state
        setCheckedInUsers(prev => {
          const filtered = prev.filter(u => u.userId !== user.id);
          return [...filtered, {
            userId: user.id,
            userName: user.user_metadata?.display_name || user.email,
            isHost: isHost,
            position: userPos,
            timestamp: new Date().toISOString()
          }];
        });
        
        toast.success(t.locationUpdated || 'Your location has been updated!');
      } catch (dbError) {
        console.error('Database update error:', dbError);
        toast.error(t.locationUpdateFailed || 'Failed to update location in database');
      }
    } catch (err) {
      console.error('Location update error:', err);
      setLocationError(err.message || t.locationError);
      toast.error(err.message || t.locationUpdateFailed);
    } finally {
      setIsUpdatingLocation(false);
    }
  };
  
  // Add this function to fetch check-ins
  const fetchCheckIns = async () => {
    try {
      // Make sure we have a party object before proceeding
      if (!party) {
        console.log('Party data not loaded yet, skipping check-ins fetch');
        return;
      }
      
      const { data, error } = await supabase
        .from('party_check_ins')
        .select('user_id, latitude, longitude, accuracy, created_at')
        .eq('party_id', id);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const checkIns = await Promise.all(data.map(async (checkIn) => {
          // Get user details
          const { data: userData } = await supabase
            .from('users')
            .select('display_name')
            .eq('id', checkIn.user_id)
            .single();
          
          return {
            userId: checkIn.user_id,
            userName: userData?.display_name || t.unknown,
            isHost: (checkIn.user_id === party.host_id || checkIn.user_id === party.host),
            position: {
              lat: checkIn.latitude,
              lng: checkIn.longitude,
              accuracy: checkIn.accuracy
            },
            timestamp: checkIn.created_at
          };
        }));
        
        setCheckedInUsers(checkIns);
      }
    } catch (err) {
      console.error('Error fetching check-ins:', err);
    }
  };
  
  // Add this to the useEffect that fetches party data
  useEffect(() => {
    if (id) {
      fetchParty();
    }
  }, [id]);
  
  // Add a separate useEffect to fetch check-ins after party data is loaded
  useEffect(() => {
    if (party && id) {
      fetchCheckIns();
    }
  }, [party, id]);

  if (loading) {
    return (
      <Container>
        <Box my={4} textAlign="center">
          <Typography>Loading party details...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '50vh',
            textAlign: 'center',
            maxWidth: '600px',
            mx: 'auto',
            p: 4,
            borderRadius: 4,
            bgcolor: 'background.paper',
            boxShadow: 1
          }}
        >
          <Typography variant="h5" color="error.main" gutterBottom fontWeight="bold">
            {error}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            {error.includes('deleted') 
              ? `The host has deleted this party. You'll be redirected to the home page ${
                  redirectCountdown ? `in ${redirectCountdown} seconds` : 'shortly'
                }.`
              : "We couldn't load the party details. Please try again later."}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/')}
            sx={{ 
              mt: 2,
              borderRadius: 8,
              px: 4
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    );
  }

  if (!party) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '50vh',
          textAlign: 'center',
          maxWidth: '600px',
          mx: 'auto',
          p: 4,
          borderRadius: 4,
          bgcolor: 'background.paper',
          boxShadow: 1
        }}
      >
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Party Not Found
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          The party you're looking for doesn't exist or has been deleted.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/')}
          sx={{ 
            mt: 2,
            borderRadius: 8,
            px: 4
          }}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  const isHost = user && party.host === user.id;
  const isAttending = party.attendees && party.attendees.includes(user?.id);

  return (
    <>
      <style>{mapStyles}</style>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        {loading ? (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '50vh' 
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : error ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '50vh',
              textAlign: 'center',
              maxWidth: '600px',
              mx: 'auto',
              p: 4,
              borderRadius: 4,
              bgcolor: 'background.paper',
              boxShadow: 1
            }}
          >
            <Typography variant="h5" color="error.main" gutterBottom fontWeight="bold">
              {error}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              {error.includes('deleted') 
                ? `The host has deleted this party. You'll be redirected to the home page ${
                    redirectCountdown ? `in ${redirectCountdown} seconds` : 'shortly'
                  }.`
                : "We couldn't load the party details. Please try again later."}
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate('/')}
              sx={{ 
                mt: 2,
                borderRadius: 8,
                px: 4
              }}
            >
              Back to Home
            </Button>
          </Box>
        ) : party ? (
          <>
            <Card 
              elevation={0}
              sx={{ 
                mb: 4, 
                borderRadius: 4,
                background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'visible'
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 16, 
                  right: 16, 
                  display: 'flex', 
                  gap: 1,
                  zIndex: 2
                }}
              >
                {(party.host_id === user.id || party.host === user.id) && (
                  <>
                    <IconButton 
                      color="inherit" 
                      onClick={() => navigate(`/edit-party/${id}`)}
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)',
                        }
                      }}
                      title={t.editParty}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      color="inherit" 
                      onClick={handleDeleteParty}
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)',
                        }
                      }}
                      title={t.deleteParty}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
                <IconButton 
                  color="inherit" 
                  onClick={() => setOpenInviteDialog(true)}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                  title={t.inviteGuests}
                >
                  <PersonAddIcon />
                </IconButton>
                {party.invite_code || party.join_code ? (
                  <PartyCodeDisplay partyCode={party.invite_code || party.join_code} />
                ) : (party.host_id === user.id || party.host === user.id) && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={async () => {
                      try {
                        // Generate a new join code
                        const { error } = await supabase
                          .from('parties')
                          .update({ join_code: generateRandomCode() })
                          .eq('id', id)
                          .select();
                          
                        if (error) throw error;
                        toast.success(t.partyCodeGenerated || 'Party code generated!');
                      } catch (err) {
                        console.error('Error generating party code:', err);
                        toast.error(t.errorGeneratingCode || 'Error generating party code');
                      }
                    }}
                    sx={{ ml: 1 }}
                  >
                    {t.generateCode || 'Generate Code'}
                  </Button>
                )}
              </Box>
              
              <CardContent sx={{ py: 5, px: { xs: 3, md: 5 } }}>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  {party.title || t.untitledParty}
                </Typography>
                
                {/* Date and Time Banner */}
                {party.date && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText'
                    }}
                  >
                    <CalendarTodayIcon fontSize="large" />
                    <Box>
                      <Typography variant="h6" fontWeight="medium">
                        {new Date(party.date).toLocaleDateString(undefined, {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </Typography>
                      <Typography variant="subtitle1">
                        {t.startingAt}: {new Date(party.date).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: language !== 'pl' // Use 24-hour format for Polish
                        })}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationOnIcon sx={{ mr: 1 }} />
                    <Typography>
                      {typeof party.location === 'object' 
                        ? (party.location.address || t.noAddressProvided) 
                        : (party.location || t.noLocationProvided)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1 }} />
                    <Typography>
                      {t.hostedBy} {(party.host_id === user.id || party.host === user.id) ? t.organizedByYou : 
                        (attendeeDetails[party.host_id || party.host]?.display_name || t.unknown)}
                    </Typography>
                  </Box>
                </Box>

                {/* RSVP Buttons */}
                <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {(party.host_id !== user.id && party.host !== user.id) && (
                    <>
                      <Button
                        variant={rsvpStatus === 'attending' ? 'contained' : 'outlined'}
                        color="success"
                        onClick={() => handleRSVP('attending')}
                        startIcon={<CheckIcon />}
                        disabled={isHost}
                        sx={{ 
                          py: { xs: 1, sm: 0.75 },
                          px: { xs: 2, sm: 1.5 },
                          fontSize: { xs: '0.9rem', sm: '0.875rem' }
                        }}
                      >
                        {t.attending}
                      </Button>
                      <Button
                        variant={rsvpStatus === 'maybe' ? 'contained' : 'outlined'}
                        color="warning"
                        onClick={() => handleRSVP('maybe')}
                        disabled={isHost}
                        sx={{ 
                          py: { xs: 1, sm: 0.75 },
                          px: { xs: 2, sm: 1.5 },
                          fontSize: { xs: '0.9rem', sm: '0.875rem' }
                        }}
                      >
                        {t.maybe}
                      </Button>
                      <Button
                        variant={rsvpStatus === 'not_attending' ? 'contained' : 'outlined'}
                        color="error"
                        onClick={() => handleRSVP('not_attending')}
                        disabled={isHost}
                        sx={{ 
                          py: { xs: 1, sm: 0.75 },
                          px: { xs: 2, sm: 1.5 },
                          fontSize: { xs: '0.9rem', sm: '0.875rem' }
                        }}
                      >
                        {t.notAttending}
                      </Button>
                    </>
                  )}
                  {(party.host_id === user.id || party.host === user.id) && (
                    <Chip 
                      label={t.youAreHosting} 
                      color="success" 
                      sx={{ 
                        borderRadius: 8,
                        px: 2,
                        py: 1,
                        bgcolor: 'success.main',
                        color: 'white',
                        fontWeight: 'bold'
                      }} 
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
            
            <Paper 
              elevation={0} 
              sx={{ 
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                mb: 4
              }}
            >
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ 
                  mb: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    minHeight: { xs: 48, sm: 48 },
                    py: { xs: 1, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    fontWeight: 500,
                    minWidth: { xs: 'auto', sm: 90 }
                  }
                }}
              >
                <Tab 
                  icon={<InfoIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />} 
                  label={t.partyDetails} 
                  iconPosition="start"
                />
                <Tab 
                  icon={<ChatIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />} 
                  label={t.chat} 
                  iconPosition="start"
                />
                <Tab 
                  icon={<ListAltIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />} 
                  label={t.items} 
                  iconPosition="start"
                />
                <Tab 
                  icon={<PersonIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />} 
                  label={t.attendeesTitle} 
                  iconPosition="start"
                />
              </Tabs>
              
              {/* Tab panels */}
              <Box sx={{ p: 3 }}>
                {tabValue === 0 && (
                  <Grid container spacing={2}>
                    {/* Map */}
                    <Grid item xs={12} md={8}>
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: { xs: 2, sm: 3 }, 
                          borderRadius: { xs: 2, sm: 3 },
                          mx: { xs: 0, sm: 0 },
                          height: { xs: 'auto', sm: '400px' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationOnIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: { xs: 22, sm: 24 } }} />
                            <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                              {t.location}
                            </Typography>
                          </Box>
                          
                          {isAttending || isHost ? (
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<MyLocationIcon />}
                              onClick={handleUpdateLocation}
                              disabled={isUpdatingLocation}
                              size="small"
                              sx={{ 
                                borderRadius: 2,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                              }}
                            >
                              {isUpdatingLocation ? (
                                <>
                                  <CircularProgress size={16} sx={{ mr: 1 }} />
                                  {t.checkingIn}
                                </>
                              ) : (
                                t.updateLocation || 'Update Location'
                              )}
                            </Button>
                          ) : null}
                        </Box>
                        
                        {isUpdatingLocation && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <AlertTitle>{t.locationAccess}</AlertTitle>
                            <Typography variant="body2">{t.locationAccessInstructions}</Typography>
                            {/* iOS-specific instructions */}
                            {isIOS() && (
                              <>
                                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium', color: 'error.main' }}>
                                  {t.locationPermissionDenied}
                                </Typography>
                                
                                {!isHttps() && (
                                  <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'error.dark', borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.contrastText' }}>
                                      {t.httpsRequired}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1, color: 'error.contrastText' }}>
                                      {t.httpsRequiredExplanation}
                                    </Typography>
                                  </Box>
                                )}
                                
                                <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.contrastText' }}>
                                    {t.locationRequired}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 1, color: 'error.contrastText' }}>
                                    {isSafari() ? t.safariLocationInstructions : t.iosLocationInstructions}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                  {!isHttps() && (
                                    <Button 
                                      variant="contained" 
                                      color="error"
                                      size="medium" 
                                      startIcon={<LockIcon />}
                                      onClick={() => {
                                        // Redirect to HTTPS version of the same URL
                                        window.location.href = window.location.href.replace('http:', 'https:');
                                      }}
                                      sx={{ px: 3, py: 1 }}
                                    >
                                      {t.switchToHttps}
                                    </Button>
                                  )}
                                  
                                  <Button 
                                    variant="contained" 
                                    color="primary"
                                    size="medium" 
                                    startIcon={<SettingsIcon />}
                                    onClick={openIOSSettings}
                                    sx={{ px: 3, py: 1 }}
                                  >
                                    {t.openSettings}
                                  </Button>
                                  
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    size="medium"
                                    startIcon={<RefreshIcon />}
                                    onClick={() => window.location.reload()}
                                    sx={{ px: 3, py: 1 }}
                                  >
                                    {t.refreshPage}
                                  </Button>
                                  
                                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', fontStyle: 'italic' }}>
                                    {t.afterSettingsInstructions}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Alert>
                        )}
                        
                        {locationError && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {locationError}
                          </Alert>
                        )}
                        
                        {party.location?.coordinates ? (
                          <Paper
                            elevation={0}
                            sx={{ 
                              height: '300px',
                              width: '100%',
                              borderRadius: 2,
                              overflow: 'hidden',
                              mb: 3
                            }}
                          >
                            <Map 
                              center={party.location.coordinates}
                              title={party.title}
                              address={party.location.address}
                              checkedInUsers={checkedInUsers}
                              CheckInMarker={CheckInMarker}
                              userLocation={userLocation}
                            />
                          </Paper>
                        ) : (
                          <Box display="flex" justifyContent="center" alignItems="center" height={{ xs: '250px', sm: '330px' }}>
                            <Typography>{t.noLocationCoordinates}</Typography>    
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                    
                    {/* Right Side Panel */}
                    <Grid item xs={12} md={4}>
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: { xs: 2, sm: 3 }, 
                          borderRadius: { xs: 2, sm: 3 },
                          mx: { xs: 0, sm: 0 },
                          mb: 3
                        }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <EventIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                            {t.partyDetails}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CalendarTodayIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                            <Typography>
                              {party.date ? (
                                new Date(party.date).toLocaleDateString(undefined, {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                              ) : (
                                t.noDateProvided
                              )}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <AccessTimeIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                            <Typography fontWeight="medium">
                              {party.date ? (
                                new Date(party.date).toLocaleTimeString(undefined, {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: language !== 'pl' // Use 24-hour format for Polish
                                })
                              ) : (
                                t.noTimeProvided
                              )}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            <LocationOnIcon sx={{ mr: 1.5, color: 'text.secondary', mt: 0.3 }} />
                            <Typography sx={{ wordBreak: 'break-word' }}>
                              {typeof party.location === 'object' 
                                ? (party.location.address || t.noAddressProvided) 
                                : (party.location || t.noLocationProvided)}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <PersonIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                            <Typography>
                              {t.hostedBy} {(party.host_id === user.id || party.host === user.id) ? t.organizedByYou : 
                                (attendeeDetails[party.host_id || party.host]?.display_name || t.unknown)}
                            </Typography>
                          </Box>
                          
                          {/* Party Description */}
                          {party.description && (
                            <Box sx={{ mt: 3, mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <DescriptionIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {t.description}
                                </Typography>
                              </Box>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  pl: 4, 
                                  whiteSpace: 'pre-wrap',
                                  color: 'text.secondary'
                                }}
                              >
                                {party.description}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
                
                {tabValue === 1 && (
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 0, 
                      height: { xs: 'calc(100vh - 220px)', sm: '500px' }, 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: { xs: 2, sm: 3 },
                      overflow: 'hidden',
                      mx: { xs: 0, sm: 0 },
                      mt: { xs: 0, sm: 0 }
                    }}
                  >
                    {/* Chat Header */}
                    <Box 
                      sx={{ 
                        p: { xs: 2, sm: 2 }, 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <SendIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: 20, sm: 24 } }} />
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                          {t.partyChat}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${party.attendees?.length || 0} ${t.participants}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </Box>
                    
                    {/* Messages */}
                    <Box 
                      sx={{ 
                        flexGrow: 1, 
                        overflow: 'auto', 
                        p: { xs: 2, sm: 2 },
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : '#f5f7fb',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {party.messages && party.messages.length > 0 ? (
                        party.messages.map((message, index) => {
                          const isCurrentUser = message.userId === user?.id;
                          const isHost = message.userId === (party.host_id || party.host);
                          
                          return (
                            <Box 
                              key={message.id || index} 
                              sx={{ 
                                width: '100%',
                                mb: { xs: 3, sm: 2 },
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isCurrentUser ? 'flex-end' : 'flex-start'
                              }}
                            >
                              {/* Message header with user info */}
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mb: 0.5,
                                  width: 'auto',
                                  px: { xs: 0.5, sm: 0 }
                                }}
                              >
                                {!isCurrentUser && (
                                  <Avatar 
                                    src={(attendeeDetails[message.userId]?.avatar_url) || (message.userPhotoURL)}
                                    sx={{ 
                                      width: { xs: 24, sm: 24 }, 
                                      height: { xs: 24, sm: 24 }, 
                                      mr: 1,
                                      bgcolor: isHost ? 'primary.main' : 'secondary.main'
                                    }}
                                  >
                                    {!attendeeDetails[message.userId]?.avatar_url && !message.userPhotoURL && 
                                      ((attendeeDetails[message.userId]?.display_name || message.userName)?.charAt(0).toUpperCase())}
                                  </Avatar>
                                )}
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ fontWeight: isHost ? 'bold' : 'normal' }}
                                >
                                  {isCurrentUser ? t.you : (attendeeDetails[message.userId]?.display_name || message.userName)}
                                  {isHost && !isCurrentUser && ` (${t.host})`}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ ml: 1 }}
                                >
                                  {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Box>
                              
                              {/* Message bubble */}
                              <Box 
                                sx={{ 
                                  maxWidth: { xs: '80%', sm: '75%' },
                                  bgcolor: isCurrentUser 
                                    ? 'primary.main' 
                                    : (theme) => theme.palette.mode === 'dark' ? 'background.paper' : '#ffffff',
                                  color: isCurrentUser ? 'primary.contrastText' : 'text.primary',
                                  borderRadius: 2,
                                  px: { xs: 2, sm: 2 },
                                  py: { xs: 1.5, sm: 1.5 },
                                  boxShadow: 1
                                }}
                              >
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    wordBreak: 'break-word',
                                    fontSize: { xs: '0.95rem', sm: '1rem' },
                                    lineHeight: { xs: 1.5, sm: 1.5 }
                                  }}
                                >
                                  {message.text}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })
                      ) : (
                        <Box 
                          display="flex" 
                          flexDirection="column" 
                          justifyContent="center" 
                          alignItems="center" 
                          height="100%"
                          sx={{ opacity: 0.7 }}
                        >
                          <SendIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
                          <Typography color="text.secondary" variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {t.noMessages}
                          </Typography>
                        </Box>
                      )}
                      <div ref={messagesEndRef} />
                    </Box>
                    
                    {/* Message Input */}
                    <Box 
                      component="form" 
                      onSubmit={handleSendMessage} 
                      sx={{ 
                        display: 'flex', 
                        p: { xs: 2, sm: 2 }, 
                        borderTop: 1, 
                        borderColor: 'divider',
                        bgcolor: 'background.paper'
                      }}
                    >
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={t.typeMessage}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={(!isAttending && !isHost) || sendingMessage}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : '#f5f7fb',
                            height: { xs: 44, sm: 40 }
                          },
                          '& .MuiOutlinedInput-input': {
                            py: { xs: 1.2, sm: 1 },
                            px: { xs: 2, sm: 1.5 },
                            fontSize: { xs: '0.95rem', sm: '0.875rem' }
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <IconButton 
                              type="submit" 
                              color="primary" 
                              disabled={!newMessage.trim() || (!isAttending && !isHost) || sendingMessage}
                              edge="end"
                              sx={{ 
                                width: { xs: 40, sm: 40 },
                                height: { xs: 40, sm: 40 }
                              }}
                            >
                              {sendingMessage ? <CircularProgress size={20} /> : <SendIcon />}
                            </IconButton>
                          )
                        }}
                      />
                    </Box>
                    {!isAttending && !isHost && (
                      <Typography 
                        variant="caption" 
                        color="error" 
                        sx={{ 
                          p: 1, 
                          textAlign: 'center', 
                          bgcolor: 'error.light', 
                          color: 'error.contrastText' 
                        }}
                      >
                        {t.mustRSVP}
                      </Typography>
                    )}
                  </Paper>
                )}
                
                {tabValue === 2 && (
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: { xs: 2, sm: 3 }, 
                      borderRadius: { xs: 2, sm: 3 },
                      mx: { xs: 0, sm: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <ListAltIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: { xs: 22, sm: 24 } }} />
                      <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        {t.itemsToBring}
                      </Typography>
                    </Box>
                    
                    {/* Add Item Form */}
                    <Box 
                      component="form" 
                      onSubmit={handleAddItem}
                      sx={{ 
                        display: 'flex', 
                        mb: 3,
                        gap: 1.5
                      }}
                    >
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder={t.addItemPlaceholder}
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        disabled={addingItem}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            height: { xs: 44, sm: 40 }
                          },
                          '& .MuiOutlinedInput-input': {
                            py: { xs: 1.2, sm: 1 },
                            px: { xs: 2, sm: 1.5 },
                            fontSize: { xs: '0.95rem', sm: '0.875rem' }
                          }
                        }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!newItemName.trim() || addingItem}
                        startIcon={addingItem ? <CircularProgress size={20} /> : <AddIcon />}
                        sx={{ 
                          borderRadius: 2,
                          px: { xs: 2, sm: 2 },
                          height: { xs: 44, sm: 40 },
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {t.add}
                      </Button>
                    </Box>
                    
                    {/* Items List */}
                    <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
                      {party.items && party.items.length > 0 ? (
                        party.items.map((item) => (
                          <ListItem
                            key={item.id}
                            secondaryAction={
                              (isHost || item.added_by === user.id) && (
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete" 
                                  onClick={() => handleRemoveItem(item)}
                                  size="small"
                                  sx={{ 
                                    mr: { xs: 0, sm: 0 },
                                    p: { xs: 1.5, sm: 1 }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )
                            }
                            sx={{ 
                              py: { xs: 1.5, sm: 1.5 },
                              px: { xs: 2, sm: 2 }
                            }}
                          >
                            <ListItemText
                              primary={item.name}
                              secondary={
                                <Typography variant="caption" color="text.secondary">
                                  {t.addedBy} {item.added_by === user.id ? t.you : (attendeeDetails[item.added_by]?.display_name || t.unknown)}
                                </Typography>
                              }
                              primaryTypographyProps={{
                                fontSize: { xs: '0.95rem', sm: '1rem' }
                              }}
                              secondaryTypographyProps={{
                                fontSize: { xs: '0.8rem', sm: '0.75rem' }
                              }}
                            />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText 
                            primary={t.noItems} 
                            primaryTypographyProps={{
                              color: 'text.secondary',
                              fontSize: { xs: '0.95rem', sm: '1rem' }
                            }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                )}
                
                {tabValue === 3 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: { xs: 2, sm: 3 }, 
                          borderRadius: { xs: 2, sm: 3 },
                          mx: { xs: 0, sm: 0 }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <PersonIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: { xs: 22, sm: 24 } }} />
                          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                            {t.attendeesTitle} ({party.attendees?.length || 0})
                          </Typography>
                        </Box>
                        
                        <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
                          {party.attendees && party.attendees.length > 0 ? (
                            party.attendees.map((attendeeId) => (
                              <ListItem
                                key={attendeeId}
                                sx={{ 
                                  py: { xs: 1.5, sm: 1.5 },
                                  px: { xs: 2, sm: 2 },
                                  bgcolor: isAttendeeHost(attendeeId) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  alignItems: { xs: 'flex-start', sm: 'center' }
                                }}
                              >
                                <Box sx={{ 
                                  display: 'flex', 
                                  width: '100%', 
                                  alignItems: 'center',
                                  mb: { xs: 1, sm: 0 }
                                }}>
                                  <Avatar 
                                    src={attendeeDetails[attendeeId]?.avatar_url}
                                    sx={{ 
                                      bgcolor: isAttendeeHost(attendeeId) ? 'primary.main' : 'secondary.main',
                                      width: { xs: 40, sm: 40 },
                                      height: { xs: 40, sm: 40 },
                                      mr: 2
                                    }}
                                  >
                                    {!attendeeDetails[attendeeId]?.avatar_url && 
                                      (attendeeDetails[attendeeId]?.display_name?.charAt(0).toUpperCase() || '?')}
                                  </Avatar>
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography 
                                        sx={{ 
                                          fontSize: { xs: '0.95rem', sm: '1rem' },
                                          fontWeight: isAttendeeHost(attendeeId) ? 'medium' : 'normal'
                                        }}
                                      >
                                        {getAttendeeDisplayName(attendeeId)}
                                      </Typography>
                                      {isAttendeeHost(attendeeId) && (
                                        <Chip 
                                          label={t.host} 
                                          color="primary" 
                                          size="small" 
                                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </Box>
                                    <Typography 
                                      variant="body2" 
                                      color="text.secondary"
                                      sx={{ fontSize: { xs: '0.8rem', sm: '0.8rem' } }}
                                    >
                                      {attendeeDetails[attendeeId]?.email}
                                    </Typography>
                                  </Box>
                                </Box>
                                
                                {/* Add Friend button - only show for other attendees who aren't already friends */}
                                {user && 
                                 attendeeId !== user.id && 
                                 !isAlreadyFriend(attendeeId) && 
                                 !friendRequestSent[attendeeId] && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<PersonAddIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                                    onClick={() => handleAddFriend(attendeeId)}
                                    sx={{ 
                                      fontSize: { xs: '0.8rem', sm: '0.8rem' },
                                      py: { xs: 0.75, sm: 0.75 },
                                      px: { xs: 1.5, sm: 1.5 },
                                      alignSelf: { xs: 'flex-start', sm: 'center' },
                                      ml: { xs: 0, sm: 'auto' }
                                    }}
                                  >
                                    {t.addFriend}
                                  </Button>
                                )}
                                {friendRequestSent[attendeeId] && (
                                  <Chip 
                                    label={t.requestSent} 
                                    color="success" 
                                    size="small" 
                                    sx={{ 
                                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                      alignSelf: { xs: 'flex-start', sm: 'center' },
                                      ml: { xs: 0, sm: 'auto' }
                                    }}
                                  />
                                )}
                              </ListItem>
                            ))
                          ) : (
                            <ListItem>
                              <ListItemText 
                                primary={t.noAttendees} 
                                primaryTypographyProps={{
                                  color: 'text.secondary',
                                  fontSize: { xs: '0.95rem', sm: '1rem' }
                                }}
                              />
                            </ListItem>
                          )}
                        </List>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Paper>
            
            {/* Invite Dialog */}
            <Dialog open={openInviteDialog} onClose={() => setOpenInviteDialog(false)}>
              <DialogTitle>{t.inviteGuests}</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label={t.email}
                  type="email"
                  fullWidth
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenInviteDialog(false)} color="inherit">
                  {t.cancel}
                </Button>
                <Button onClick={handleInvite} color="primary">
                  {t.invite}
                </Button>
              </DialogActions>
            </Dialog>
            
            {/* Add Item Dialog */}
            <Dialog open={openItemDialog} onClose={() => !addingItem && setOpenItemDialog(false)}>
              <DialogTitle>Add Item</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Item Name"
                  fullWidth
                  variant="outlined"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={addingItem}
                />
                <FormControl fullWidth variant="outlined" disabled={addingItem}>
                  <InputLabel>Who's bringing it?</InputLabel>
                  <Select
                    value={newItemBroughtBy}
                    onChange={(e) => setNewItemBroughtBy(e.target.value)}
                    label="Who's bringing it?"
                  >
                    <MenuItem value="">
                      <em>I'm bringing it</em>
                    </MenuItem>
                    <MenuItem value="Someone else (TBD)">Someone else (TBD)</MenuItem>
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenItemDialog(false)} disabled={addingItem}>Cancel</Button>
                <Button 
                  onClick={handleAddItem} 
                  variant="contained" 
                  color="primary"
                  disabled={!newItemName.trim() || addingItem}
                  startIcon={addingItem ? <CircularProgress size={20} /> : null}
                >
                  {addingItem ? 'Adding...' : 'Add Item'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        ) : (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '50vh',
              textAlign: 'center',
              maxWidth: '600px',
              mx: 'auto',
              p: 4,
              borderRadius: 4,
              bgcolor: 'background.paper',
              boxShadow: 1
            }}
          >
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Party Not Found
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              The party you're looking for doesn't exist or has been deleted.
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate('/')}
              sx={{ 
                mt: 2,
                borderRadius: 8,
                px: 4
              }}
            >
              Back to Home
            </Button>
          </Box>
        )}
      </Container>
    </>
  );
};

export default PartyDetail; 